import {
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    ActivatedRoute,
    Router,
    RouterOutlet,
} from '@angular/router';

import { NavigationService } from '#root/core/navigation/navigation.service';
import { Navigation } from '#root/core/navigation/navigation.types';
import {
    Subject,
    takeUntil,
} from 'rxjs';

import { NotificationsComponent } from '#root/layout/common/notifications/notifications.component';
import { SchemeToggleComponent } from '#root/layout/common/scheme-toggle/scheme-toggle.component';
import { UserComponent } from '#root/layout/common/user/user.component';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { environment } from 'environments/environment';
import { QuickChatComponent } from '#root/layout/common/quick-chat/quick-chat.component';

@Component({
    selector: 'dense-layout',
    templateUrl: './dense.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        FuseFullscreenComponent,
        SchemeToggleComponent,
        NotificationsComponent,
        UserComponent,
        RouterOutlet,
        CommonModule,
        QuickChatComponent,
    ]
})
export class DenseLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    navigationAppearance: 'default' | 'dense' = 'dense';
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    logo = '../../../../../images/logo/logo-favicon.png';
    logoIso = '../../../../../images/logo/logo-favicon.png';
    logoNormal = '../../../../../corporate_logos/smart_business_logo_white_letters.png';
    isLogoIso = true;
    appVersion = environment.version;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {

                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');

                // Change the navigation appearance
                this.navigationAppearance = this.isScreenSmall ? 'default' : 'dense';
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Toggle the navigation appearance
     */
    toggleNavigationAppearance(): void {
        if (this.navigationAppearance === 'dense') {
            this.logo = this.logo != this.logoIso ?
                this.logoIso : this.logoNormal;
            if (this.logo === this.logoIso) {
                this.isLogoIso = true;
            } else {
                this.isLogoIso = false;
            }
        } else {
            this.logo = this.logo != this.logoNormal ?
                this.logoNormal : this.logoIso;
            if (this.logo === this.logoNormal) {
                this.isLogoIso = false;
            } else {
                this.isLogoIso = true;
            }
        }
        this.navigationAppearance = (this.navigationAppearance === 'default' ? 'dense' : 'default');
    }
}
